import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Building2, MapPin, Store, Calculator, CreditCard, Receipt,
  Clock, Users, Package, Monitor, Settings, CheckCircle2,
  ChevronRight, ChevronLeft, Loader2, Wine, ShoppingCart,
  UtensilsCrossed, Briefcase, HelpCircle
} from 'lucide-react';

const INDUSTRIES = [
  { id: 'liquor', name: 'Liquor Store', icon: Wine, description: 'Age verification, case-break, bottle deposit' },
  { id: 'grocery', name: 'Grocery Store', icon: ShoppingCart, description: 'Weighted items, produce, EBT support' },
  { id: 'restaurant', name: 'Restaurant', icon: UtensilsCrossed, description: 'Tips, tables, kitchen display' },
  { id: 'convenience', name: 'Convenience Store', icon: Store, description: 'Fast checkout, lottery, tobacco' },
  { id: 'retail', name: 'Retail Shop', icon: Briefcase, description: 'General retail, clothing, electronics' },
  { id: 'service', name: 'Service Business', icon: HelpCircle, description: 'Services, appointments, invoicing' },
];

const SETUP_STEPS = [
  { id: 'business_profile', name: 'Business Profile', icon: Building2 },
  { id: 'store_location', name: 'Store Location', icon: MapPin },
  { id: 'industry_type', name: 'Industry Type', icon: Store },
  { id: 'tax_settings', name: 'Tax Settings', icon: Calculator },
  { id: 'payment_processor', name: 'Payment Processor', icon: CreditCard },
  { id: 'receipt_settings', name: 'Receipt Settings', icon: Receipt },
  { id: 'business_hours', name: 'Business Hours', icon: Clock },
  { id: 'employees', name: 'Employees', icon: Users },
  { id: 'inventory_import', name: 'Inventory Import', icon: Package },
  { id: 'hardware_setup', name: 'Hardware Setup', icon: Monitor },
  { id: 'closeout_preferences', name: 'Closeout Preferences', icon: Settings },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(null);
  const [formData, setFormData] = useState({
    business_profile: {
      business_name: '',
      legal_name: '',
      business_type: '',
      tax_id: '',
      phone: '',
      email: '',
      website: ''
    },
    store_location: {
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US'
    },
    industry_type: '',
    tax_settings: {
      enable_tax: true,
      tax_rate: 0,
      tax_name: 'Sales Tax',
      tax_included_in_price: false
    },
    payment_processor: {
      processor_type: 'stripe',
      test_mode: true
    },
    receipt_settings: {
      header_text: '',
      footer_text: 'Thank you for your business!',
      show_logo: true,
      show_address: true,
      show_phone: true,
      receipt_width: '80mm'
    },
    business_hours: {
      monday_open: '09:00', monday_close: '21:00',
      tuesday_open: '09:00', tuesday_close: '21:00',
      wednesday_open: '09:00', wednesday_close: '21:00',
      thursday_open: '09:00', thursday_close: '21:00',
      friday_open: '09:00', friday_close: '22:00',
      saturday_open: '09:00', saturday_close: '22:00',
      sunday_open: '10:00', sunday_close: '20:00',
      is_24_7: false
    },
    closeout_preferences: {
      require_manager_approval: true,
      email_closeout_report: true,
      cash_drawer_warning_threshold: 500
    }
  });

  useEffect(() => {
    const loadSetupProgress = async () => {
      if (!user?.store_id) {
        toast.error('No store assigned. Please contact support.');
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get('/setup/wizard/progress', {
          params: { store_id: user.store_id }
        });
        setProgress(response.data);
        
        // If completed, redirect to dashboard
        if (response.data.is_completed) {
          toast.success('Setup already completed!');
          navigate('/dashboard');
          return;
        }
        
        // Find first incomplete step
        const completedSteps = response.data.steps.filter(s => s.status === 'completed');
        setCurrentStep(completedSteps.length);
        
        // Load existing data if any
        if (response.data.business_profile) {
          setFormData(prev => ({
            ...prev,
            business_profile: response.data.business_profile
          }));
        }
        if (response.data.store_location) {
          setFormData(prev => ({
            ...prev,
            store_location: response.data.store_location
          }));
        }
        if (response.data.industry_type) {
          setFormData(prev => ({
            ...prev,
            industry_type: response.data.industry_type
          }));
        }
      } catch (error) {
        // If wizard not started, start it
        if (error.response?.status === 404) {
          try {
            await api.post('/setup/wizard/start', { store_id: user?.store_id });
            // Reload progress after starting
            const response = await api.get('/setup/wizard/progress', {
              params: { store_id: user.store_id }
            });
            setProgress(response.data);
          } catch (startError) {
            toast.error('Failed to start setup wizard');
          }
        } else {
          toast.error('Failed to load setup progress');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadSetupProgress();
  }, [navigate, user?.store_id]);

  const saveStep = async (stepData) => {
    setSaving(true);
    try {
      await api.post('/setup/wizard/step', {
        step: SETUP_STEPS[currentStep].id,
        data: stepData,
        status: 'completed'
      });
      
      toast.success(`${SETUP_STEPS[currentStep].name} saved!`);
      
      if (currentStep < SETUP_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        toast.success('Setup completed! Welcome to MezgebPOS!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    const stepId = SETUP_STEPS[currentStep].id;
    saveStep(formData[stepId]);
  };

  const handleSkip = async () => {
    try {
      await api.post('/setup/wizard/step', {
        step: SETUP_STEPS[currentStep].id,
        data: {},
        status: 'skipped'
      });
      
      if (currentStep < SETUP_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to skip step');
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const renderStepContent = () => {
    const step = SETUP_STEPS[currentStep];
    
    switch (step.id) {
      case 'business_profile':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Tell us about your business</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Business Name *</label>
                <input
                  type="text"
                  value={formData.business_profile.business_name}
                  onChange={(e) => updateFormData('business_profile', 'business_name', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="Your Store Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Legal Name</label>
                <input
                  type="text"
                  value={formData.business_profile.legal_name}
                  onChange={(e) => updateFormData('business_profile', 'legal_name', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="Legal Business Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Business Type</label>
                <select
                  value={formData.business_profile.business_type}
                  onChange={(e) => updateFormData('business_profile', 'business_type', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                >
                  <option value="">Select type...</option>
                  <option value="llc">LLC</option>
                  <option value="corporation">Corporation</option>
                  <option value="sole_proprietorship">Sole Proprietorship</option>
                  <option value="partnership">Partnership</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tax ID (EIN)</label>
                <input
                  type="text"
                  value={formData.business_profile.tax_id}
                  onChange={(e) => updateFormData('business_profile', 'tax_id', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="XX-XXXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.business_profile.phone}
                  onChange={(e) => updateFormData('business_profile', 'phone', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.business_profile.email}
                  onChange={(e) => updateFormData('business_profile', 'email', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="store@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="url"
                  value={formData.business_profile.website}
                  onChange={(e) => updateFormData('business_profile', 'website', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="www.yourstore.com"
                />
              </div>
            </div>
          </div>
        );
        
      case 'store_location':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Where is your store located?</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Street Address *</label>
                <input
                  type="text"
                  value={formData.store_location.address}
                  onChange={(e) => updateFormData('store_location', 'address', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input
                    type="text"
                    value={formData.store_location.city}
                    onChange={(e) => updateFormData('store_location', 'city', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <input
                    type="text"
                    value={formData.store_location.state}
                    onChange={(e) => updateFormData('store_location', 'state', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                  <input
                    type="text"
                    value={formData.store_location.zip_code}
                    onChange={(e) => updateFormData('store_location', 'zip_code', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'industry_type':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">What type of business do you run?</h3>
            <p className="text-gray-600 mb-4">We'll customize MezgebPOS features for your industry.</p>
            <div className="grid grid-cols-2 gap-4">
              {INDUSTRIES.map((industry) => {
                const Icon = industry.icon;
                const isSelected = formData.industry_type === industry.id;
                return (
                  <button
                    key={industry.id}
                    onClick={() => setFormData(prev => ({ ...prev, industry_type: industry.id }))}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      isSelected 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <Icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="font-medium">{industry.name}</div>
                    <div className="text-sm text-gray-500">{industry.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 'tax_settings':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Configure your tax settings</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enable_tax"
                  checked={formData.tax_settings.enable_tax}
                  onChange={(e) => updateFormData('tax_settings', 'enable_tax', e.target.checked)}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="enable_tax" className="font-medium">Enable Sales Tax</label>
              </div>
              
              {formData.tax_settings.enable_tax && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax Name</label>
                      <input
                        type="text"
                        value={formData.tax_settings.tax_name}
                        onChange={(e) => updateFormData('tax_settings', 'tax_name', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                        placeholder="Sales Tax"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.tax_settings.tax_rate}
                        onChange={(e) => updateFormData('tax_settings', 'tax_rate', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                        placeholder="8.25"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="tax_included"
                      checked={formData.tax_settings.tax_included_in_price}
                      onChange={(e) => updateFormData('tax_settings', 'tax_included_in_price', e.target.checked)}
                      className="w-4 h-4 mr-2"
                    />
                    <label htmlFor="tax_included">Tax is included in displayed prices</label>
                  </div>
                </>
              )}
            </div>
          </div>
        );
        
      case 'payment_processor':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Choose your payment processor</h3>
            <div className="space-y-4">
              <div className="p-4 border-2 border-blue-600 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <input
                    type="radio"
                    id="stripe"
                    checked={formData.payment_processor.processor_type === 'stripe'}
                    onChange={() => updateFormData('payment_processor', 'processor_type', 'stripe')}
                    className="w-4 h-4 mr-2"
                  />
                  <label htmlFor="stripe" className="font-semibold">Stripe (Recommended)</label>
                </div>
                <p className="text-sm text-gray-600 ml-6">
                  Secure, reliable payment processing. Accept credit cards, debit cards, and digital wallets.
                </p>
              </div>
              
              <div className="p-4 border-2 border-gray-200 rounded-lg opacity-60">
                <div className="flex items-center mb-2">
                  <input
                    type="radio"
                    id="helcim"
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label htmlFor="helcim" className="font-semibold">Helcim (Coming Soon)</label>
                </div>
                <p className="text-sm text-gray-600 ml-6">
                  Interchange-plus pricing with no monthly fees. Available soon.
                </p>
              </div>
              
              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  id="test_mode"
                  checked={formData.payment_processor.test_mode}
                  onChange={(e) => updateFormData('payment_processor', 'test_mode', e.target.checked)}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="test_mode">Enable test mode (for training)</label>
              </div>
            </div>
          </div>
        );
        
      case 'receipt_settings':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Customize your receipts</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Receipt Header</label>
                <textarea
                  value={formData.receipt_settings.header_text}
                  onChange={(e) => updateFormData('receipt_settings', 'header_text', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  rows={2}
                  placeholder="Thank you for shopping with us!"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Receipt Footer</label>
                <textarea
                  value={formData.receipt_settings.footer_text}
                  onChange={(e) => updateFormData('receipt_settings', 'footer_text', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  rows={2}
                  placeholder="Thank you for your business!"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_address"
                    checked={formData.receipt_settings.show_address}
                    onChange={(e) => updateFormData('receipt_settings', 'show_address', e.target.checked)}
                    className="w-4 h-4 mr-2"
                  />
                  <label htmlFor="show_address">Show store address on receipt</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_phone"
                    checked={formData.receipt_settings.show_phone}
                    onChange={(e) => updateFormData('receipt_settings', 'show_phone', e.target.checked)}
                    className="w-4 h-4 mr-2"
                  />
                  <label htmlFor="show_phone">Show phone number on receipt</label>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'business_hours':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Set your business hours</h3>
            <div className="space-y-2">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <div key={day} className="flex items-center gap-4 p-2 hover:bg-gray-50 rounded">
                  <span className="w-24 capitalize font-medium">{day}</span>
                  <input
                    type="time"
                    value={formData.business_hours[`${day}_open`]}
                    onChange={(e) => updateFormData('business_hours', `${day}_open`, e.target.value)}
                    className="px-2 py-1 border rounded"
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={formData.business_hours[`${day}_close`]}
                    onChange={(e) => updateFormData('business_hours', `${day}_close`, e.target.value)}
                    className="px-2 py-1 border rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'employees':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Add your team</h3>
            <p className="text-gray-600">You can add employees now or skip and add them later in Settings.</p>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Employee management is available in Settings {'>'} Employees</p>
            </div>
          </div>
        );
        
      case 'inventory_import':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Import your inventory</h3>
            <p className="text-gray-600">You can import products from a CSV file or add them manually later.</p>
            <div className="p-4 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 text-center">
                Use Inventory Import in the sidebar to upload your product list
              </p>
            </div>
          </div>
        );
        
      case 'hardware_setup':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Set up your hardware</h3>
            <p className="text-gray-600 mb-4">Check off the hardware you've installed:</p>
            <div className="space-y-2">
              {[
                { id: 'touchscreen', name: 'Touchscreen Terminal', required: true },
                { id: 'scanner', name: 'Barcode Scanner', required: true },
                { id: 'receipt_printer', name: 'Receipt Printer', required: true },
                { id: 'cash_drawer', name: 'Cash Drawer', required: true },
                { id: 'card_reader', name: 'Card Reader', required: true },
                { id: 'label_printer', name: 'Label Printer', required: false },
                { id: 'customer_display', name: 'Customer Display', required: false },
                { id: 'tablet', name: 'Tablet/Backup Device', required: false },
              ].map((item) => (
                <div key={item.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    id={item.id}
                    className="w-4 h-4 mr-3"
                  />
                  <label htmlFor={item.id} className="flex-1">
                    {item.name}
                    {item.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'closeout_preferences':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Configure end-of-day closeout</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="require_manager"
                  checked={formData.closeout_preferences.require_manager_approval}
                  onChange={(e) => updateFormData('closeout_preferences', 'require_manager_approval', e.target.checked)}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="require_manager">Require manager approval for closeout</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="email_report"
                  checked={formData.closeout_preferences.email_closeout_report}
                  onChange={(e) => updateFormData('closeout_preferences', 'email_closeout_report', e.target.checked)}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="email_report">Email closeout report to owner</label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cash Drawer Warning Threshold ($)</label>
                <input
                  type="number"
                  value={formData.closeout_preferences.cash_drawer_warning_threshold}
                  onChange={(e) => updateFormData('closeout_preferences', 'cash_drawer_warning_threshold', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg"
                  placeholder="500"
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  const StepIcon = SETUP_STEPS[currentStep].icon;
  const progressPercentage = progress ? progress.progress_percentage : 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Welcome to MezgebPOS!</h1>
                <p className="text-muted-foreground">Let's get your store set up in just a few minutes.</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground mb-1">Setup Progress</div>
                <div className="text-2xl font-bold text-cyan-500">{progressPercentage}%</div>
              </div>
            </div>
          
            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          
            {/* Step Indicators */}
            <div className="flex justify-between mt-4 overflow-x-auto gap-2">
              {SETUP_STEPS.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;
                const StepIcon = step.icon;
                
                return (
                  <div
                    key={step.id}
                    className={`flex flex-col items-center min-w-[80px] ${
                      isCurrent ? 'text-cyan-400' : isCompleted ? 'text-emerald-400' : 'text-muted-foreground'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                      isCurrent ? 'bg-cyan-500/20' : isCompleted ? 'bg-emerald-500/20' : 'bg-muted'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span className="text-xs text-center">{step.name}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Current Step */}
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <StepIcon className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{SETUP_STEPS[currentStep].name}</h2>
                <p className="text-muted-foreground text-sm">Step {currentStep + 1} of {SETUP_STEPS.length}</p>
              </div>
            </div>

          {renderStepContent()}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleSkip}
              >
                Skip for now
              </Button>
              
              <Button
                onClick={handleNext}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : currentStep === SETUP_STEPS.length - 1 ? (
                  <>
                    Complete Setup
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </Layout>
  );
}
